import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Modal } from 'react-bootstrap';
import styled from 'styled-components';
import { Button } from '@spark/ui-components/components/Button';
import { cpfValido } from '../hooks/useProgramacaoHavanListagem';
import { mascararTelefone } from './ProgramacaoHavanVxPesquisaModal';

const mascararCpf = valor => {
  const numeros = String(valor || '').replace(/\D/g, '').slice(0, 11);
  return numeros
    .replace(/^(\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1-$2');
};

export const descreverAmbienteTms = ambiente => (ambiente === 'producao' ? 'produção' : 'homologação');

/**
 * Motorista que não está no TMS. modo 'usar' (veio da busca): a programação segue com CPF, nome e celular e,
 * se marcado, o motorista também é criado no TMS. modo 'cadastrar' (veio do selo): só cria no TMS.
 * O TMS exige o mínimo: CPF, nome e se é terceiro ou próprio.
 */
const ProgramacaoHavanMotoristaForaTmsModal = ({ show, modo, cpfInicial, nomeInicial, telefoneInicial, cadastroTms, onClose, onSubmit }) => {
  const [cpf, setCpf] = useState('');
  const [nome, setNome] = useState('');
  const [telefone, setTelefone] = useState('');
  const [cadastrarNoTms, setCadastrarNoTms] = useState(true);
  const [terceiro, setTerceiro] = useState(true);
  const [tentouSalvar, setTentouSalvar] = useState(false);
  const somenteTms = modo === 'cadastrar';
  const tmsDisponivel = Boolean(cadastroTms?.habilitado);

  useEffect(() => {
    if (!show) return;
    setCpf(mascararCpf(cpfInicial));
    setNome(nomeInicial || '');
    setTelefone(mascararTelefone(telefoneInicial || ''));
    setCadastrarNoTms(true);
    setTerceiro(true);
    setTentouSalvar(false);
  }, [show, cpfInicial, nomeInicial, telefoneInicial]);

  const digitosCpf = cpf.replace(/\D/g, '');
  const erroCpf = !digitosCpf ? 'Informe o CPF.' : !cpfValido(digitosCpf) ? 'CPF inválido. Confira os dígitos.' : '';
  const erroNome = nome.trim().length < 3 ? 'Informe o nome do motorista.' : '';
  const digitosTelefone = telefone.replace(/\D/g, '');
  const erroTelefone = digitosTelefone && digitosTelefone.length < 10 ? 'Celular incompleto: DDD + número.' : '';
  const valido = !erroCpf && !erroNome && !erroTelefone;
  const enviarAoTms = tmsDisponivel && (somenteTms || cadastrarNoTms);

  const handleSubmit = event => {
    event.preventDefault();
    setTentouSalvar(true);
    if (!valido) return;
    onSubmit?.({ cpf: digitosCpf, nome: nome.trim().toUpperCase(), telefone: digitosTelefone || null, cadastrarNoTms: enviarAoTms, terceiro });
  };

  return (
    <Modal show={show} onHide={onClose} centered>
      <Form onSubmit={handleSubmit} noValidate>
        <Modal.Header closeButton>
          <Modal.Title>{somenteTms ? 'Cadastrar motorista no TMS' : 'Motorista fora do TMS'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {somenteTms ? null : (
            <Aviso>
              O motorista não foi encontrado no TMS. A programação segue com estes dados e a consulta VX continua disponível.
              Quando o CPF aparecer no TMS, a linha passa a usar o cadastro de lá.
            </Aviso>
          )}
          <Form.Group controlId="motoristaForaTmsCpf" className="mb-3">
            <Form.Label>CPF</Form.Label>
            <Form.Control value={cpf} inputMode="numeric" autoFocus={!cpfInicial} placeholder="000.000.000-00" isInvalid={tentouSalvar && Boolean(erroCpf)} onChange={event => setCpf(mascararCpf(event.target.value))} />
            <Form.Control.Feedback type="invalid">{erroCpf}</Form.Control.Feedback>
          </Form.Group>
          <Form.Group controlId="motoristaForaTmsNome" className="mb-3">
            <Form.Label>Nome</Form.Label>
            <Form.Control value={nome} maxLength={255} autoFocus={Boolean(cpfInicial)} isInvalid={tentouSalvar && Boolean(erroNome)} onChange={event => setNome(event.target.value)} />
            <Form.Control.Feedback type="invalid">{erroNome}</Form.Control.Feedback>
          </Form.Group>
          <Form.Group controlId="motoristaForaTmsTelefone">
            <Form.Label>Celular <Opcional>(opcional)</Opcional></Form.Label>
            <Form.Control value={telefone} inputMode="tel" placeholder="(47) 99999-9999" isInvalid={tentouSalvar && Boolean(erroTelefone)} onChange={event => setTelefone(mascararTelefone(event.target.value))} />
            <Form.Control.Feedback type="invalid">{erroTelefone}</Form.Control.Feedback>
          </Form.Group>

          {tmsDisponivel ? (
            <SecaoTms>
              {somenteTms ? null : (
                <Form.Check
                  id="motoristaForaTmsCadastrar"
                  type="checkbox"
                  label="Cadastrar também no TMS"
                  checked={cadastrarNoTms}
                  onChange={event => setCadastrarNoTms(event.target.checked)}
                />
              )}
              {enviarAoTms ? (
                <>
                  <TipoMotorista role="radiogroup" aria-label="Tipo de motorista">
                    <Form.Check inline id="motoristaForaTmsTerceiro" type="radio" name="motoristaForaTmsTipo" label="Terceiro" checked={terceiro} onChange={() => setTerceiro(true)} />
                    <Form.Check inline id="motoristaForaTmsProprio" type="radio" name="motoristaForaTmsTipo" label="Próprio" checked={!terceiro} onChange={() => setTerceiro(false)} />
                  </TipoMotorista>
                  <NotaTms $homologacao={cadastroTms?.ambiente !== 'producao'}>
                    Ambiente do TMS: <strong>{descreverAmbienteTms(cadastroTms?.ambiente)}</strong>.
                    {' '}O cadastro é o mínimo que o TMS exige (CPF, nome e tipo); os demais dados podem ser completados no TMS depois.
                  </NotaTms>
                </>
              ) : null}
            </SecaoTms>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" variant="secondary" onClick={onClose}>Cancelar</Button>
          <Button type="submit" variant="primary">{somenteTms ? 'Cadastrar no TMS' : 'Usar este motorista'}</Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

ProgramacaoHavanMotoristaForaTmsModal.propTypes = {
  show: PropTypes.bool,
  modo: PropTypes.oneOf(['usar', 'cadastrar']),
  cpfInicial: PropTypes.string,
  nomeInicial: PropTypes.string,
  telefoneInicial: PropTypes.string,
  cadastroTms: PropTypes.shape({ habilitado: PropTypes.bool, ambiente: PropTypes.string }),
  onClose: PropTypes.func,
  onSubmit: PropTypes.func
};

ProgramacaoHavanMotoristaForaTmsModal.defaultProps = {
  show: false,
  modo: 'usar',
  cpfInicial: '',
  nomeInicial: '',
  telefoneInicial: '',
  cadastroTms: { habilitado: false, ambiente: '' },
  onClose: undefined,
  onSubmit: undefined
};

export default ProgramacaoHavanMotoristaForaTmsModal;

const Aviso = styled.div`
  margin-bottom: 14px;
  padding: 10px 12px;
  border: 1px solid #e0b44c;
  border-radius: 4px;
  background: #fff8e6;
  color: #6b4a00;
  font-size: 13px;
`;
const Opcional = styled.span`color: #8a96a3; font-weight: 400;`;
const SecaoTms = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #e2e8f0;
`;
const TipoMotorista = styled.div`font-size: 13px;`;
export const NotaTms = styled.div`
  padding: 8px 10px;
  border: 1px solid ${({ $homologacao }) => ($homologacao ? '#c6b3e6' : '#9fd3ad')};
  border-radius: 4px;
  background: ${({ $homologacao }) => ($homologacao ? '#f7f3fd' : '#f1faf3')};
  color: #334155;
  font-size: 12px;
`;
